import React from "react";
import "./checkbox-list.scss";

export interface CheckboxListItem {
  /** Unique id for the option. */
  id: string;
  /** Label shown next to the checkbox. */
  label: string;
  /** Whether this option is checked. */
  checked: boolean;
  /** Optional disabled state for this item. */
  disabled?: boolean;
}

export interface CheckboxListProps {
  /** List of checkbox options. Each can have id, label, checked, disabled. */
  items: CheckboxListItem[];
  /** Called when any checkbox (including Select All) changes. Receives updated list and the id that changed. */
  onSelectionChange?: (items: CheckboxListItem[], changedId: string) => void;
  /** Label for the "Select All" row. Omit or set to empty to hide Select All. */
  selectAllLabel?: string;
  /** Whether to show the Select All row. Default true when selectAllLabel is non-empty. */
  showSelectAll?: boolean;
  /** Container width (e.g. "17.5rem", "100%"). Default 17.5rem. */
  width?: string | number;
  /** Vertical padding (rem) for each row. Default 0.625 (matches Select option spacing). */
  rowPaddingY?: number;
  /** Gap (rem) between checkbox and label. Default 0.75. */
  checkboxLabelGap?: number;
  /** Horizontal padding (rem) of the root. Default 1. */
  paddingX?: number;
  /** Vertical padding (rem) of the root. Default 0.625. */
  paddingY?: number;
  /** Border radius (rem) of the inner list container. Default 0.25. */
  borderRadius?: number;
  /** Background color of the root. Default from design: #ffffff. */
  backgroundColor?: string;
  /** Label color. Default from design: #3d3d3d (Primary Gray 4). */
  labelColor?: string;
  /** Checkbox unchecked border color. Default #d6d6d6 (Primary Gray 14). */
  checkboxBorderColor?: string;
  /** Checkbox checked background/border color. Default #0f0f0f (Primary Gray 1). */
  checkboxCheckedColor?: string;
  /** Checkmark color when checked. Default #ffffff. */
  checkmarkColor?: string;
  /** Font weight for Select All label: "bold" | "regular". Default "bold". */
  selectAllLabelFontWeight?: "bold" | "regular";
  /** Font size (rem) for labels. Default 0.875. */
  labelFontSize?: number;
  /** Line height (rem) for item labels. Default 1.125. */
  itemLabelLineHeight?: number;
  /** Line height (rem) for Select All label. Default 1.375. */
  selectAllLabelLineHeight?: number;
  /** Optional class for the root. */
  className?: string;
  /** Optional style for the root. */
  style?: React.CSSProperties;
  /** Optional aria-label for the list. */
  ariaLabel?: string;
}

function CheckmarkIcon({ className, color }: { className?: string; color?: string }) {
  return (
    <svg
      className={className}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M2 6L5 9L10 3"
        stroke={color ?? "currentColor"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const CheckboxList: React.FC<CheckboxListProps> = ({
  items,
  onSelectionChange,
  selectAllLabel = "Select All",
  showSelectAll = true,
  width = "17.5rem",
  rowPaddingY = 0.625,
  checkboxLabelGap = 0.75,
  paddingX = 1,
  paddingY = 0.625,
  borderRadius = 0.25,
  backgroundColor,
  labelColor,
  checkboxBorderColor,
  checkboxCheckedColor,
  checkmarkColor,
  selectAllLabelFontWeight = "bold",
  labelFontSize = 0.875,
  itemLabelLineHeight = 1.125,
  selectAllLabelLineHeight = 1.375,
  className = "",
  style,
  ariaLabel = "Checkbox list",
}) => {
  const allChecked = items.length > 0 && items.every((i) => i.checked);
  const showSelectAllRow = showSelectAll && selectAllLabel !== "";

  const handleItemChange = (id: string, checked: boolean) => {
    const next = items.map((item) =>
      item.id === id ? { ...item, checked } : item
    );
    onSelectionChange?.(next, id);
  };

  const handleSelectAllChange = (checked: boolean) => {
    const next = items.map((item) => ({ ...item, checked }));
    onSelectionChange?.(next, "select-all");
  };

  const rootStyle: React.CSSProperties = {
    padding: `${paddingY}rem ${paddingX}rem`,
    ...(backgroundColor && { backgroundColor }),
    ...style,
  };

  const listStyle: React.CSSProperties = {
    width: typeof width === "number" ? `${width}rem` : width,
    borderRadius: `${borderRadius}rem`,
  };

  const rowStyle: React.CSSProperties = {
    paddingTop: `${rowPaddingY}rem`,
    paddingBottom: `${rowPaddingY}rem`,
  };

  const gapStyle: React.CSSProperties = {
    gap: `${checkboxLabelGap}rem`,
  };

  const itemLabelStyle: React.CSSProperties = {
    fontSize: `${labelFontSize}rem`,
    lineHeight: `${itemLabelLineHeight}rem`,
    ...(labelColor && { color: labelColor }),
  };

  const selectAllLabelStyle: React.CSSProperties = {
    fontSize: `${labelFontSize}rem`,
    lineHeight: `${selectAllLabelLineHeight}rem`,
    fontWeight: selectAllLabelFontWeight === "bold" ? 700 : 400,
    ...(labelColor && { color: labelColor }),
  };

  return (
    <div
      className={`checkbox-list ${className}`}
      style={rootStyle}
      role="group"
      aria-label={ariaLabel}
    >
      <div className="checkbox-list__inner" style={listStyle}>
        <div className="checkbox-list__list">
          {showSelectAllRow && (
            <div
              className="checkbox-list__row"
              style={{ ...rowStyle, ...gapStyle }}
            >
              <button
                type="button"
                role="checkbox"
                aria-checked={allChecked}
                aria-label={selectAllLabel}
                className={`checkbox-list__box ${allChecked ? "checkbox-list__box--checked" : ""}`}
                style={{
                  ...(checkboxBorderColor && !allChecked && { borderColor: checkboxBorderColor }),
                  ...(allChecked && checkboxCheckedColor && { backgroundColor: checkboxCheckedColor, borderColor: checkboxCheckedColor }),
                }}
                onClick={() => handleSelectAllChange(!allChecked)}
              >
                {allChecked && (
                  <span className="checkbox-list__check" style={checkmarkColor ? { color: checkmarkColor } : undefined}>
                    <CheckmarkIcon color={checkmarkColor} />
                  </span>
                )}
              </button>
              <span className="checkbox-list__label checkbox-list__label--select-all" style={selectAllLabelStyle}>
                {selectAllLabel}
              </span>
            </div>
          )}

          {items.map((item) => (
            <div
              key={item.id}
              className="checkbox-list__row"
              style={{ ...rowStyle, ...gapStyle }}
            >
              <button
                type="button"
                role="checkbox"
                aria-checked={item.checked}
                aria-label={item.label}
                aria-disabled={item.disabled}
                disabled={item.disabled}
                className={`checkbox-list__box ${item.checked ? "checkbox-list__box--checked" : ""} ${item.disabled ? "checkbox-list__box--disabled" : ""}`}
                style={{
                  ...(checkboxBorderColor && !item.checked && { borderColor: checkboxBorderColor }),
                  ...(item.checked && checkboxCheckedColor && { backgroundColor: checkboxCheckedColor, borderColor: checkboxCheckedColor }),
                }}
                onClick={() => !item.disabled && handleItemChange(item.id, !item.checked)}
              >
                {item.checked && (
                  <span className="checkbox-list__check" style={checkmarkColor ? { color: checkmarkColor } : undefined}>
                    <CheckmarkIcon color={checkmarkColor} />
                  </span>
                )}
              </button>
              <span
                className="checkbox-list__label"
                style={itemLabelStyle}
                onClick={() => !item.disabled && handleItemChange(item.id, !item.checked)}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
